import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import InvoicePreview from './InvoicePreview';
import '../assets/InvoiceTemplateSettings.css';

const InvoiceTemplateSettings = () => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    headerText: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    footerText: '',
    paymentInstructions: '',
    bankName: '',
    accountNumber: '',
    accountName: '',
    customNotes: ''
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    fetchTemplateSettings();
  }, []);

  const fetchTemplateSettings = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getTemplateSettings();
      setTemplate(response.data);
      
      // Populate form with existing data
      setFormData({
        headerText: response.data.headerText || '',
        companyAddress: response.data.companyAddress || '',
        companyPhone: response.data.companyPhone || '',
        companyEmail: response.data.companyEmail || '',
        footerText: response.data.footerText || '',
        paymentInstructions: response.data.paymentInstructions || '',
        bankName: response.data.bankName || '',
        accountNumber: response.data.accountNumber || '',
        accountName: response.data.accountName || '',
        customNotes: response.data.customNotes || ''
      });
      
      // Logo is now a separate endpoint (was stripped from settings to save bandwidth)
      // Fetch it only if the server says a logo exists
      if (response.data.hasLogo) {
        try {
          const logoResponse = await adminApi.getTemplateLogo();
          setLogoPreview(logoResponse.data.companyLogo);
        } catch (logoErr) {
          console.warn('Could not load logo preview:', logoErr.message);
        }
      } else {
        setLogoPreview(null);
      }
      
      setError('');
    } catch (err) {
      console.error('Failed to fetch template settings:', err);
      setError('Failed to load template settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file.');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo file size must be less than 2MB.');
      return;
    }
    
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
    setError('');
  };

  const handleUploadLogo = async () => {
    if (!logoFile && !logoPreview) {
      setError('Please select a logo to upload.');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      const response = await adminApi.uploadTemplateLogo({
        logoData: logoPreview,
        fileName: logoFile?.name || 'logo.png'
      });
      
      // Backend now returns { message, hasLogo, logoFileName } — not the full template
      // logoPreview already holds the base64 from the file reader, so no update needed
      setTemplate(prev => ({ ...prev, hasLogo: response.data.hasLogo }));
      setSuccess('Logo uploaded successfully!');
      setLogoFile(null);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to upload logo:', err);
      setError(err.response?.data?.message || 'Failed to upload logo. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the company logo?')) {
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      await adminApi.removeTemplateLogo();
      // Backend returns { message, hasLogo: false }
      setTemplate(prev => ({ ...prev, hasLogo: false }));
      setLogoPreview(null);
      setLogoFile(null);
      setSuccess('Logo removed successfully!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to remove logo:', err);
      setError(err.response?.data?.message || 'Failed to remove logo. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      
      const response = await adminApi.updateTemplateSettings(formData);
      // Backend returns settings with hasLogo (not companyLogo) — merge carefully
      setTemplate(prev => ({ ...response.data, hasLogo: prev?.hasLogo }));
      setSuccess('Template settings saved successfully!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError(err.response?.data?.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  // Generate sample invoice data for preview
  const getSampleInvoiceData = () => {
    return {
      lessons: [
        {
          date: new Date(),
          tutee: { name: 'Sample Student' },
          tutor: { name: 'Sample Tutor' },
          subject: 'Mathematics',
          duration: 60,
          rate: 50,
          tutorRate: 35
        }
      ],
      adjustments: [],
      recurringPayments: [],
      totals: {
        lessonTotal: 50,
        adjustmentTotal: 0,
        recurringTotal: 0,
        finalAmount: 50
      }
    };
  };

  if (loading) {
    return (
      <div className="template-settings-loading">
        <div className="spinner"></div>
        <p>Loading template settings...</p>
      </div>
    );
  }

  return (
    <div className="invoice-template-settings">
      <div className="settings-header">
        <h1>Invoice Template Settings</h1>
        <p className="settings-description">
          Customize your invoice templates with company branding and payment information
        </p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <div className="settings-content">
        {/* Company Logo Section */}
        <div className="settings-section">
          <h2>Company Logo</h2>
          <div className="logo-upload-section">
            <div className="logo-preview-container">
              {logoPreview ? (
                <div className="logo-preview">
                  <img src={logoPreview} alt="Company Logo" />
                </div>
              ) : (
                <div className="logo-placeholder">
                  <span>No logo uploaded</span>
                </div>
              )}
            </div>
            
            <div className="logo-actions">
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleLogoChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="logo-upload" className="btn btn-secondary">
                Choose Logo
              </label>
              
              {logoFile && (
                <button 
                  className="btn btn-primary"
                  onClick={handleUploadLogo}
                  disabled={saving}
                >
                  {saving ? 'Uploading...' : 'Upload Logo'}
                </button>
              )}
              
              {logoPreview && !logoFile && (
                <button 
                  className="btn btn-danger"
                  onClick={handleRemoveLogo}
                  disabled={saving}
                >
                  Remove Logo
                </button>
              )}
            </div>
            
            <p className="help-text">
              Recommended: PNG or JPG, max 2MB, transparent background preferred
            </p>
          </div>
        </div>

        {/* Header Section */}
        <div className="settings-section">
          <h2>Header Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="headerText">Company Name</label>
              <input
                type="text"
                id="headerText"
                name="headerText"
                value={formData.headerText}
                onChange={handleInputChange}
                placeholder="JJ Tutorial Singapore"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="companyAddress">Company Address</label>
              <textarea
                id="companyAddress"
                name="companyAddress"
                value={formData.companyAddress}
                onChange={handleInputChange}
                placeholder="Enter company address"
                rows="3"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="companyPhone">Phone Number</label>
              <input
                type="text"
                id="companyPhone"
                name="companyPhone"
                value={formData.companyPhone}
                onChange={handleInputChange}
                placeholder="+65 1234 5678"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="companyEmail">Email Address</label>
              <input
                type="email"
                id="companyEmail"
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleInputChange}
                placeholder="contact@jjtutorial.com"
              />
            </div>
          </div>
        </div>

        {/* Payment Instructions Section */}
        <div className="settings-section">
          <h2>Payment Instructions</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="paymentInstructions">Payment Instructions</label>
              <textarea
                id="paymentInstructions"
                name="paymentInstructions"
                value={formData.paymentInstructions}
                onChange={handleInputChange}
                placeholder="Please make payment via bank transfer..."
                rows="3"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="bankName">Bank Name</label>
              <input
                type="text"
                id="bankName"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                placeholder="DBS Bank"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="accountNumber">Account Number</label>
              <input
                type="text"
                id="accountNumber"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                placeholder="123-456789-0"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="accountName">Account Name</label>
              <input
                type="text"
                id="accountName"
                name="accountName"
                value={formData.accountName}
                onChange={handleInputChange}
                placeholder="JJ Tutorial Pte Ltd"
              />
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="settings-section">
          <h2>Footer Information</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="footerText">Footer Text</label>
              <textarea
                id="footerText"
                name="footerText"
                value={formData.footerText}
                onChange={handleInputChange}
                placeholder="Thank you for your business!"
                rows="2"
              />
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="customNotes">Custom Notes</label>
              <textarea
                id="customNotes"
                name="customNotes"
                value={formData.customNotes}
                onChange={handleInputChange}
                placeholder="Add any additional notes or terms..."
                rows="3"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="settings-actions">
          <button 
            className="btn btn-secondary"
            onClick={handlePreview}
          >
            Preview Template
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="preview-modal">
          <div className="preview-modal-content">
            <div className="preview-modal-header">
              <h2>Invoice Template Preview</h2>
              <button 
                className="close-btn"
                onClick={() => setShowPreview(false)}
              >
                ×
              </button>
            </div>
            <div className="preview-modal-body">
              <InvoicePreview
                invoiceType="tutor"
                recipient={{ name: 'Sample Tutor', email: 'tutor@example.com' }}
                month={new Date().getMonth() + 1}
                year={new Date().getFullYear()}
                invoiceData={getSampleInvoiceData()}
                templateSettings={{
                  ...formData,
                  companyLogo: logoPreview
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceTemplateSettings;
